import { useState, useEffect } from 'preact/hooks';
import { useMutation } from 'react-query';
import { downloadFileFromBase64 } from 'utils/download.utils';
import { ShipmentDocumentType } from '@pperform/pp-data-mappers';
import { useCurrentShipment } from 'components/MerchantTrackingWidget/hooks/useCurrentShipment';
import {
  getShippingAttachment,
  populateReturnShipmentEventsForDemoAccount,
} from '../../services/ShipmentDetails.service';

// ─── FLOW OVERVIEW ───────────────────────────────────────────────────────────
//
// [ERROR]   Any user-initiated action failed (refetch failed OR download threw)
//           → show error message; no button
//
// [SUCCESS] Download completed
//           → show success label; no button
//
// [LOADING] API fetch or file download in-flight
//           → show disabled button with spinner
//
// [NON-QR]  Label type is non-QR (PDF, ZPL, XLS, EPL, etc.)
//           MIME type from API response (always provided by BFF)
//           Base64 is validated before download (atob + minimum size check)
//           to prevent downloading corrupt files to the user's device.
//           ├─ valid base64  → button click → downloadFileFromBase64(base64) — no XHR
//           ├─ invalid base64 → download blocked, error shown
//           └─ absent        → button click → refetch → downloadFileFromBase64(fresh) | error
//
// [QR]      Label type is QR code
//           Download always avoids XHR to presigned S3 URLs to prevent CORS
//           failures caused by CloudFront caching responses without
//           Access-Control-Allow-Origin (see docs/cors-qr-issue.md).
//
//           Render + Download — base64 is validated via isValidBase64() before
//           use. This catches corrupt/truncated base64 (e.g. "iVBORw0KGgo..."
//           cut off mid-stream fails atob()). Only validated base64 is exposed
//           via qrImageSrc and allowed for download.
//
//           ├─ valid base64  → <img src="data:image/png;base64,..."> + download
//           ├─ invalid base64 → image hidden, download blocked, error shown
//           ├─ base64 absent → refetch → re-validate | error
//           └─ image failed  → error
//
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validates that a base64 string decodes into meaningful binary content.
 * Returns false if atob() throws (invalid base64 characters) or if the
 * decoded payload is too small to be a real file (< 50 bytes).
 */
function isValidBase64(base64: string): boolean {
  try {
    return atob(base64).length >= 50;
  } catch {
    return false;
  }
}

type AttachmentData = Awaited<ReturnType<typeof getShippingAttachment>>;

interface UseShipmentLabelParams {
  orgId: number;
  shipmentPk: number;
}

interface UseShipmentLabelResult {
  // true when document type is shipping_label_qr
  isQR: boolean;
  // src for the QR <img>: a validated base64 data URL. undefined until the mount
  // fetch resolves AND the base64 passes isValidBase64().
  qrImageSrc: string | undefined;
  // true when base64 content exists but failed isValidBase64() validation.
  // Used by the component to explicitly guard rendering.
  qrImageError: boolean;
  // true while GET /documents/labels OR a file download is in-flight
  isLoading: boolean;
  // true when the error UI should be shown:
  //   - user-initiated refetch failed, OR
  //   - download base64 threw
  // Silent mount-time fetch failures are NOT surfaced — button stays visible for retry
  showError: boolean;
  // true after a successful download; stays true (button replaced by success label)
  downloadSuccess: boolean;
  // click handler for non-QR label button (PDF etc.)
  handleNonQRClick: () => Promise<void>;
  // click handler for QR button
  handleQRClick: () => Promise<void>;
}

export const useShipmentLabel = ({
  orgId,
  shipmentPk,
}: UseShipmentLabelParams): UseShipmentLabelResult => {
  const { shipment, updateShipment } = useCurrentShipment();

  // The shipping label is a one-shot fetch with manual retry — no cached server
  // state to share or background-refetch — so modelled as a mutation. The same
  // mutation backs the mount fetch, user-triggered retries, and pre-download
  // refetches. mutateAsync rejects on failure and resolves with the fetched data,
  // so there is no stale-data-on-error risk.
  const {
    mutateAsync: fetchLabel,
    data: attachmentData,
    isLoading: isFetchingLabel,
  } = useMutation({
    mutationFn: (): Promise<AttachmentData> =>
      getShippingAttachment(orgId, shipmentPk),
  });

  // Initial fetch to determine document type and pre-load content. Failure is
  // intentionally silent — the button stays visible and acts as the retry trigger.
  // fetchLabel is stable, so this runs exactly once.
  useEffect(() => {
    fetchLabel().catch(() => undefined);
  }, [fetchLabel]);

  // labelRefetchError is set only when a user-initiated refetch fails.
  // Silent mount-time fetch failures leave it false so the button stays as retry.
  const [labelRefetchError, setLabelRefetchError] = useState(false);
  // null = not attempted, true = download failed, false = download succeeded
  const [downloadLabelError, setDownloadLabelError] = useState<boolean | null>(
    null,
  );

  const isQR = attachmentData?.type === ShipmentDocumentType.shipping_label_qr;
  const base64Content = attachmentData?.base64Content;

  // qrImageSrc is only set when the base64 content passes isValidBase64().
  // This gates both rendering (no broken image placeholder) and download
  // (no corrupt file). Corrupt/truncated base64 like
  // "iVBORw0KGgoAAAAN7+UnuLt86r2X..." fails atob() and is rejected here.
  const qrImageValid = !!base64Content && isValidBase64(base64Content);
  const qrImageError = !!base64Content && !qrImageValid;
  const qrImageSrc: string | undefined = qrImageValid
    ? `data:${attachmentData?.mimeType};base64,${base64Content}`
    : undefined;

  // Fetch fresh label data for a user-initiated action.
  // On failure: sets labelRefetchError and returns undefined.
  const fetchFreshData = async (): Promise<AttachmentData | undefined> => {
    try {
      return await fetchLabel();
    } catch {
      setLabelRefetchError(true);
      return undefined;
    }
  };

  // Shared download logic for both QR and non-QR labels.
  // Resolves data (cached or fresh), then triggers download.
  const downloadLabel = async (needsFresh: boolean): Promise<void> => {
    const data = needsFresh ? await fetchFreshData() : attachmentData;
    if (!data) return; // fetchFreshData already set labelRefetchError

    // If the fetch succeeded but base64 content is missing or corrupt, surface
    // the error. isValidBase64 catches both invalid base64 characters (atob throws)
    // and truncated payloads too small to be a real file.
    if (!data.base64Content || !isValidBase64(data.base64Content)) {
      setDownloadLabelError(true);
      return;
    }

    const isSuccess = downloadFileFromBase64(
      data.base64Content,
      data.mimeType,
      data.fileName,
    );
    setDownloadLabelError(!isSuccess);
    if (isSuccess) {
      updateShipment(populateReturnShipmentEventsForDemoAccount(shipment));
    }
  };

  const handleNonQRClick = async (): Promise<void> => {
    return downloadLabel(!base64Content);
  };

  // If the image validation failed (corrupt base64), refetch even if base64 is cached.
  const handleQRClick = async (): Promise<void> => {
    return downloadLabel(!base64Content || qrImageError);
  };

  return {
    isQR,
    qrImageSrc,
    qrImageError,
    isLoading: isFetchingLabel,
    showError: labelRefetchError || downloadLabelError === true,
    downloadSuccess: downloadLabelError === false,
    handleNonQRClick,
    handleQRClick,
  };
};
